import { Button, Dialog, Grid, MenuItem, Select, TextField, Tooltip } from '@material-ui/core';
import React, { ReactElement } from 'react';

// api
import { ECompanyType, getCompanies, ICompany } from '~/src/dash/api/company';
// views
import { IPriorityOption } from '~/src/dash/api/headerbidders/utils_headerbidder';
// components
import { CompanyMultiSelect } from '~/src/dash/components/dropdowns/company-multi-select';
import { ITab, SettingsTabs } from '~/src/dash/components/settings-tabs';
// other
import { IWithStylesProps } from '~/src/dash/types/props';
// utils
import { decorate } from '~/src/dash/utils/decorator';
// This component is used to add / edit companies when opened currently from the company-multi-select

export interface IProps extends IWithStylesProps {
  active: boolean;
  forcedSelection: ECompanyType;
  publisherID: string;
  onClose: () => void;
  onDelete: (id: number) => void;
  onUpdate: (name: string, companyType: ECompanyType, ID: number) => void;
  // increments when there is a company api call to refresh
  counter: number;
  username: string;
}

export const companyTypes = [
  { value: ECompanyType.TYPE_UNKNOWN, text: 'Select a Company Type' },
  { value: ECompanyType.TYPE_ADVERTISER, text: 'Advertiser' },
  { value: ECompanyType.TYPE_AGENCY, text: 'Agency' },
  { value: ECompanyType.TYPE_AD_NETWORK, text: 'Ad Network' },
  { value: ECompanyType.TYPE_HOUSE_ADVERTISER, text: 'House Advertiser' },
];

// Groups of similar types of company types.
// Add grouping here to replace the default options. If company type doesn't exist
// in this grouping, companyTypes will be used as the default options.
const companyGroupings: { [key: string]: ECompanyType[] } = {
  [ECompanyType.TYPE_ADVERTISER]: [
    ECompanyType.TYPE_ADVERTISER,
    ECompanyType.TYPE_HOUSE_ADVERTISER,
  ],
};

interface IState {
  ID: number;
  addName: string;
  addCompanyType: ECompanyType;
  addHelperText: string;
  addCompanyTypeError: boolean;
  editName: string;
  editCompanyType: ECompanyType;
  editHelperText: string;
  editCompanyTypeError: boolean;
  deleteAdvertiserIDs: number[];
  deleteAgencyIDs: number[];
  deleteHelperText: string;
  deleteName: string;
  companies: ICompany[];
}

export const AddEditCompanyModel = decorate(
  {
    styles: (theme: any) => ({
      section: {
        display: 'block',
        marginTop: 20,
        marginBottom: 10,
        width: '100%',
      },
      tab: {
        marginTop: 15,
        width: '100%',
      },
      sectionLowMargin: {
        display: 'block',
        marginBottom: theme.spacing.unit * 3,
        maxWidth: 500,
      },
      dialogPaper: {
        height: '60vh',
        padding: 30,
        display: 'flex',
      },
      selectedTitle: {
        background: '#d7d8fc 0% 0% no-repeat padding-box',
        borderRadius: 5,
        color: '#4952FF',
        fontSize: 16,
        fontWeight: 500,
        padding: 8,
      },
      unselectedTitle: {
        '&:hover': {
          background: '#d7d8fc 0% 0% no-repeat padding-box',
          borderRadius: 5,
          color: '#4952FF',
          fontSize: 16,
          fontWeight: 500,
          padding: 8,
        },
        color: '#707070',
        textDecoration: 'none',
        fontSize: 16,
        fontWeight: 500,
        padding: 8,
      },
    }),
  },
  class extends React.Component<IProps, IState> {
    state = {
      ID: 0,
      addName: '',
      addHelperText: '',
      addCompanyType: ECompanyType.TYPE_UNKNOWN,
      addCompanyTypeError: false,
      editName: '',
      editHelperText: '',
      editCompanyType: ECompanyType.TYPE_UNKNOWN,
      editCompanyTypeError: false,
      deleteAdvertiserIDs: [] as number[],
      deleteAgencyIDs: [] as number[],
      deleteHelperText: '',
      deleteName: '',
      companies: [] as ICompany[],
    };

    componentDidMount() {
      this.getCompanies();
    }

    componentWillReceiveProps(nextProps: IProps) {
      if (nextProps.counter !== this.props.counter) {
        this.getCompanies();
      }
    }

    getCompanies() {
      getCompanies(this.props.publisherID).then((companies: ICompany[]) => {
        this.setState({
          companies,
          ID: 0,
          addName: '',
          addHelperText: '',
          addCompanyType: ECompanyType.TYPE_UNKNOWN,
          addCompanyTypeError: false,
          editName: '',
          editHelperText: '',
          editCompanyType: ECompanyType.TYPE_UNKNOWN,
          editCompanyTypeError: false,
          deleteAdvertiserIDs: [] as number[],
          deleteAgencyIDs: [] as number[],
          deleteHelperText: '',
          deleteName: '',
        });
      });
    }

    handleChangeAddName = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      this.setState(
        {
          addName: target.value,
        },
        () => {
          if (this.state.addHelperText !== '' && this.state.addName !== '') {
            this.setState({
              addHelperText: '',
            });
          }
        },
      );
    };

    handleChangeAddCompanyType = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      const companyType = parseInt(target.value, 10);
      this.setState({
        addCompanyType: companyType === undefined ? ECompanyType.TYPE_ADVERTISER : companyType,
        addCompanyTypeError: false,
      });
    };

    handleChangeEditName = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      this.setState(
        {
          editName: target.value,
        },
        () => {
          if (this.state.editHelperText !== '' && this.state.editName !== '') {
            this.setState({
              editHelperText: '',
            });
          }
        },
      );
    };

    handleChangeEditCompanyType = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      const companyType = parseInt(target.value, 10);
      this.setState({
        editCompanyType: companyType === undefined ? ECompanyType.TYPE_ADVERTISER : companyType,
        editCompanyTypeError: false,
      });
    };

    onClose = () => {
      if (this.props.onClose) {
        this.props.onClose();
      }

      // Reset the name input
      this.setState({
        addName: '',
      });
    };

    onDelete = () => {
      if (this.state.deleteAdvertiserIDs.length) {
        this.setState({
          deleteHelperText: `A company must not be used as any orders' advertiser before it can be deleted.`,
        });
        return;
      }
      if (this.props.onDelete && this.state.deleteName !== '') {
        this.props.onDelete(this.state.ID);
      }
    };

    // Is called right before modal is opened
    onEntering = () => {
      const { forcedSelection } = this.props;
      const { addCompanyType } = this.state;

      let addCompanyValue = addCompanyType;
      // If we are forcing a type of selection and there's a group for it, then allow the user
      // to pick one from the group, otherwise just use the forced selection
      if (forcedSelection && companyGroupings[forcedSelection]) {
        // Whenever the modal loads up, reset the default option to the first one
        addCompanyValue = companyGroupings[forcedSelection][0];
      } else if (forcedSelection) {
        addCompanyValue = forcedSelection;
      }

      this.setState({
        addCompanyType: addCompanyValue,
      });
    };

    // onUpdate validates the company type and name before parent saves
    onUpdateAdd = () => {
      if (this.state.addName === '') {
        this.setState({
          addHelperText: 'Please enter a name before saving',
        });
      } else if (this.state.addCompanyType === ECompanyType.TYPE_UNKNOWN) {
        this.setState({
          addCompanyTypeError: true,
        });
      } else {
        if (this.props.onUpdate) {
          this.props.onUpdate(this.state.addName, this.state.addCompanyType, 0);
        }
      }
    };

    // onUpdate validates the company type and name before parent saves
    onUpdateEdit = () => {
      if (this.state.editName === '') {
        this.setState({
          editHelperText: 'Please enter a name before saving',
        });
      } else if (this.state.editCompanyType === ECompanyType.TYPE_UNKNOWN) {
        this.setState({
          editCompanyTypeError: true,
        });
      } else {
        if (this.props.onUpdate) {
          this.props.onUpdate(this.state.editName, this.state.editCompanyType, this.state.ID);
        }
      }
    };

    handleChangeAddCompany = (companyIDs: number[]) => {
      if (companyIDs.length > 0) {
        const company = this.state.companies.find((c: ICompany) => c.ID === companyIDs[0]);
        if (company) {
          this.setState({
            ID: company.ID,
            addName: company.Name,
            addCompanyType: company.CompanyType,
            addCompanyTypeError: false,
          });
        }
      }
    };

    handleChangeEditCompany = (companyIDs: number[]) => {
      if (companyIDs.length > 0) {
        const company = this.state.companies.find((c: ICompany) => c.ID === companyIDs[0]);
        if (company) {
          this.setState({
            ID: company.ID,
            editName: company.Name,
            editCompanyType: company.CompanyType,
            editCompanyTypeError: false,
          });
        }
      }
    };

    handleChangeDeleteCompany = (companyIDs: number[]) => {
      if (companyIDs.length > 0) {
        const company = this.state.companies.find((c: ICompany) => c.ID === companyIDs[0]);
        if (company) {
          this.setState({
            ID: company.ID,
            deleteAdvertiserIDs: company.OrderAdvertiserIDs || [],
            deleteAgencyIDs: company.OrderAgencyIDs || [],
            deleteHelperText: '',
            deleteName: company.Name,
          });
        }
      }
    };

    renderAdd = () => {
      const { classes, forcedSelection } = this.props;
      const { addName, addHelperText, addCompanyTypeError, addCompanyType } = this.state;

      return (
        <Grid item style={{ height: '100%' }}>
          <Grid
            container
            justify='space-between'
            alignItems='flex-start'
            direction='column'
            style={{ height: '100%' }}
          >
            <Grid item style={{ width: '100%' }}>
              <Grid
                container
                justify='flex-start'
                alignItems='flex-start'
                direction='column'
                style={{ height: '100%' }}
              >
                {/* Name */}
                <Grid item className={classes.section}>
                  <Grid container direction='row' alignItems='center'>
                    <Grid item xs={3}>
                      Name
                    </Grid>
                    <Grid item xs={5}>
                      <TextField
                        name='Name'
                        fullWidth
                        value={addName}
                        onChange={this.handleChangeAddName}
                        error={addHelperText !== ''}
                        helperText={addHelperText}
                        inputProps={{
                          style: { fontSize: 14 },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Company Type */}
                <Grid item className={classes.section}>
                  <Grid container direction='row' alignItems='center'>
                    <Grid item xs={3}>
                      Company Type
                    </Grid>
                    <Grid item xs={5}>
                      <Select
                        name='CompanyType'
                        label='Company Type'
                        value={addCompanyType}
                        onChange={this.handleChangeAddCompanyType}
                        // If a grouping exists, then don't disable it
                        disabled={!companyGroupings[forcedSelection]}
                        style={{
                          fontSize: '14px',
                          width: '100%',
                          color: addCompanyTypeError ? 'red' : 'black',
                        }}
                      >
                        {
                          // If there's a predefined grouping...
                          companyGroupings[forcedSelection]
                            ? // Then return the grouping
                              companyGroupings[forcedSelection].map((companyType: ECompanyType) => (
                                <MenuItem value={companyTypes[companyType].value}>
                                  {companyTypes[companyType].text}
                                </MenuItem>
                              ))
                            : // Otherwise return all the other available options
                              companyTypes.map((option: IPriorityOption) => (
                                <MenuItem value={option.value}>{option.text}</MenuItem>
                              ))
                        }
                      </Select>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Grid item style={{ marginBottom: 30, width: '100%' }}>
              <Grid container justify='center' direction='column'>
                {/* Buttons */}
                <Grid item>
                  <Grid container direction='row' justify='flex-end' alignItems='center'>
                    <Grid item>
                      <Button onClick={this.onClose} color='primary' autoFocus>
                        Cancel
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button onClick={this.onUpdateAdd} color='primary' autoFocus>
                        Save
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderEdit = () => {
      const { classes, counter, publisherID } = this.props;
      const { ID, editName, editHelperText, editCompanyTypeError, editCompanyType } = this.state;
      return (
        <Grid item style={{ height: '100%' }}>
          <Grid
            container
            justify='space-between'
            alignItems='flex-start'
            direction='column'
            style={{ height: '100%' }}
          >
            <Grid item style={{ width: '100%' }}>
              <Grid
                container
                justify='flex-start'
                alignItems='flex-start'
                direction='column'
                style={{ height: '100%' }}
              >
                <Grid item className={classes.section}>
                  <Grid container direction='row' alignItems='center'>
                    <Grid item xs={3}>
                      Select Company
                    </Grid>
                    <Grid item xs={5}>
                      <CompanyMultiSelect
                        onChange={this.handleChangeEditCompany}
                        publisherID={publisherID}
                        companyIDs={ID ? [ID] : []}
                        enableMultiSelect={false}
                        counter={counter}
                        menuTitle='Company'
                        username={this.props.username}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                {/* Name */}
                <Grid item className={classes.section}>
                  <Grid container direction='row' alignItems='center'>
                    <Grid item xs={3}>
                      Name
                    </Grid>
                    <Grid item xs={5}>
                      <TextField
                        name='Name'
                        fullWidth
                        value={editName}
                        onChange={this.handleChangeEditName}
                        error={editHelperText !== ''}
                        helperText={editHelperText}
                        inputProps={{
                          style: { fontSize: 14 },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                {/* Company Type */}
                <Grid item className={classes.section}>
                  <Grid container direction='row' alignItems='center'>
                    <Grid item xs={3}>
                      Company Type
                    </Grid>
                    <Grid item xs={5}>
                      <Select
                        name='CompanyType'
                        label='Company Type'
                        value={editCompanyType}
                        onChange={this.handleChangeEditCompanyType}
                        style={{
                          fontSize: '14px',
                          width: '100%',
                          color: editCompanyTypeError ? 'red' : 'black',
                        }}
                      >
                        {companyTypes.map((option: IPriorityOption) => (
                          <MenuItem value={option.value}>{option.text}</MenuItem>
                        ))}
                      </Select>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid item style={{ marginBottom: 30, width: '100%' }}>
              <Grid container justify='center' direction='column'>
                {/* Buttons */}
                <Grid item>
                  <Grid container direction='row' justify='flex-end' alignItems='center'>
                    <Grid item>
                      <Button onClick={this.onClose} color='primary' autoFocus>
                        Cancel
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button onClick={this.onUpdateEdit} color='primary' autoFocus>
                        Save
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderDelete = () => {
      const { classes, counter, publisherID } = this.props;
      const { ID } = this.state;
      return (
        <Grid item style={{ height: '100%' }}>
          <Grid
            container
            justify='space-between'
            alignItems='flex-start'
            direction='column'
            style={{ height: '100%' }}
          >
            <Grid item style={{ width: '100%' }}>
              <Grid
                container
                justify='flex-start'
                alignItems='flex-start'
                direction='column'
                style={{ height: '100%' }}
              >
                <Grid item className={classes.section}>
                  <Grid container direction='row' alignItems='center'>
                    <Grid item xs={3}>
                      Select Company
                    </Grid>
                    <Grid item xs={5}>
                      <CompanyMultiSelect
                        onChange={this.handleChangeDeleteCompany}
                        publisherID={publisherID}
                        companyIDs={ID ? [ID] : []}
                        enableMultiSelect={false}
                        counter={counter}
                        menuTitle='Company'
                        username={this.props.username}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                {!!this.state.deleteAdvertiserIDs.length && (
                  <Grid item className={classes.section} style={{ color: 'red' }}>
                    {`Orders using ${
                      this.state.deleteName
                    } as Advertiser: ${this.state.deleteAdvertiserIDs.join(', ')}`}
                  </Grid>
                )}
                {!!this.state.deleteAgencyIDs.length && (
                  <Grid item className={classes.section}>
                    {`Orders using ${
                      this.state.deleteName
                    } as Agency: ${this.state.deleteAgencyIDs.join(', ')}`}
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item style={{ marginBottom: 30, width: '100%' }}>
              <Grid container justify='center' direction='column'>
                {/* Buttons */}
                <Grid item>
                  <Grid container direction='row' justify='flex-end' alignItems='center'>
                    <Grid item>
                      <Button onClick={this.onClose} color='primary' autoFocus>
                        Cancel
                      </Button>
                    </Grid>
                    <Grid item>
                      <Tooltip
                        title={
                          this.state.deleteAdvertiserIDs.length ? this.state.deleteHelperText : ''
                        }
                      >
                        <Button
                          onClick={this.onDelete}
                          disabled={!!this.state.deleteAdvertiserIDs.length}
                          color='primary'
                          autoFocus
                        >
                          Delete
                        </Button>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    // define tabs and corresponding render functions
    renderTabs = (): ReactElement => {
      const tabs: ITab[] = [
        {
          label: 'Add Company',
          render: this.renderAdd,
        },
        {
          label: 'Edit Company',
          render: this.renderEdit,
        },
        {
          label: 'Delete Company',
          render: this.renderDelete,
        },
      ];
      return <SettingsTabs marginTop={0} paddingRight={10} tabs={tabs} />;
    };

    render() {
      const { classes } = this.props;
      return (
        <Dialog
          classes={{ paper: classes.dialogPaper }}
          fullWidth
          maxWidth='md'
          open={this.props.active}
          onClose={this.onClose}
          TransitionProps={{
            onEntering: this.onEntering,
          }}
          aria-labelledby='alert-dialog-title'
          aria-describedby='alert-dialog-description'
        >
          <Grid item style={{ height: '100%' }}>
            <Grid container justify='space-between' direction='column' style={{ height: '100%' }}>
              {this.renderTabs()}
            </Grid>
          </Grid>
        </Dialog>
      );
    }
  },
);
